// Kindra Health · Concept C · Challenger / Generic-vs-Kindra · PREVIEW ONLY
//
// Direction: high-conversion challenger. Kindra vs generic AI / Google,
// shown as a real-time split-screen. The hero is literally the aha: same
// question, two answers — generic vs contextual. Below the hero, more
// comparison rows (blood results · medication · specialist prep) hammer
// the gap home. Typography-driven, high confidence, low ornamentation.
//
// Visual personality: split 50/50 layout; left = pale red "generic"
// side, right = teal "Kindra" side. Bold serif headlines. No fake
// endorsements. Coral accent on the "generic" side so the contrast is
// instantly legible at a glance.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../services/activation';
import {
  PAPER, NAVY, NAVY_MUTED, NAVY_SOFT, TEAL, TEAL_SOFT, HAIRLINE, SAGE_BG, CORAL, CORAL_SOFT,
  ArrowRight, CheckTick, XMark,
  ConceptNav, ConceptFooter,
} from './_shared';

// ─── Hero · split comparison ─────────────────────────────
const HeroC = ({ onStart }) => (
  <section className="relative overflow-hidden" style={{ backgroundColor: PAPER }} data-testid="c-hero">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-20 lg:pt-24 pb-14 md:pb-20">
      <div className="max-w-[880px]">
        <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-5" style={{ color: TEAL, letterSpacing: '0.26em' }} data-testid="c-hero-eyebrow">
          The difference is context
        </p>
        <h1
          className="font-serif leading-[0.98] tracking-[-0.022em] font-normal text-[48px] sm:text-[64px] lg:text-[80px] xl:text-[88px]"
          style={{ color: NAVY }}
          data-testid="c-hero-headline"
        >
          Generic answers aren't enough<br />
          when your health is <em className="italic" style={{ color: TEAL }}>personal.</em>
        </h1>
        <p className="font-sans mt-7 md:mt-9 text-[16px] md:text-[18.5px] leading-[1.55] max-w-[640px]" style={{ color: NAVY_MUTED }} data-testid="c-hero-subhead">
          The same question, same day, same person. On the left: every generic tool. On the right: Kindra — answering in light of your reports, medications and upcoming care.
        </p>
      </div>

      {/* Split-screen answer */}
      <div
        className="mt-12 md:mt-16 rounded-3xl overflow-hidden"
        style={{ boxShadow: '0 40px 80px -30px rgba(14,28,46,0.28)', border: `1px solid ${HAIRLINE}` }}
        data-testid="c-hero-split"
      >
        {/* Shared question bar */}
        <div className="flex items-center gap-3 px-5 md:px-7 py-4 md:py-5" style={{ backgroundColor: SAGE_BG, borderBottom: `1px solid ${HAIRLINE}` }}>
          <span className="font-sans text-[10.5px] uppercase" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>You asked</span>
          <p className="flex-1 font-serif text-[17px] md:text-[20px]" style={{ color: NAVY }}>"Why am I still exhausted?"</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Generic side */}
          <div className="p-7 md:p-10 lg:border-r" style={{ backgroundColor: '#FFFFFF', borderColor: HAIRLINE }} data-testid="c-hero-generic">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: CORAL_SOFT }}>
                <XMark size={12} color={CORAL} />
              </span>
              <span className="font-sans text-[10.5px] uppercase font-medium" style={{ color: CORAL, letterSpacing: '0.22em' }}>Generic AI · Google</span>
            </div>
            <p className="font-serif text-[20px] md:text-[23px] leading-[1.45]" style={{ color: NAVY_MUTED }}>
              "Fatigue can be caused by many things — poor sleep, stress, iron deficiency, thyroid issues, depression. Consider seeing your doctor for tests."
            </p>
            <div className="mt-7 pt-5 space-y-1.5" style={{ borderTop: `1px solid ${HAIRLINE}` }}>
              {[
                'Doesn\'t know your ferritin was low in March',
                'Doesn\'t know you started Sertraline 3 weeks ago',
                'Doesn\'t know your GP visit is Friday',
                'Doesn\'t know which questions are worth asking',
              ].map((l, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <XMark size={11} color={CORAL} />
                  <p className="font-sans text-[13px]" style={{ color: NAVY_SOFT }}>{l}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Kindra side */}
          <div className="p-7 md:p-10" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="c-hero-kindra">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: TEAL_SOFT }}>
                <CheckTick size={12} color={TEAL} />
              </span>
              <span className="font-sans text-[10.5px] uppercase font-medium" style={{ color: TEAL, letterSpacing: '0.22em' }}>Kindra</span>
            </div>
            <p className="font-serif text-[20px] md:text-[23px] leading-[1.45]" style={{ color: PAPER }}>
              "Your ferritin was low in March, fatigue has worsened, and your GP visit is Friday. I've added three questions and your recent results to your appointment brief."
            </p>
            <div className="mt-7 pt-5 space-y-1.5" style={{ borderTop: '1px solid rgba(251,250,246,0.12)' }}>
              {[
                'Reads your March iron studies',
                'Knows your medication history',
                'Knows your Friday GP visit',
                'Prepares the 3 questions worth asking',
              ].map((l, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckTick size={12} color={TEAL} />
                  <p className="font-sans text-[13px]" style={{ color: 'rgba(251,250,246,0.82)' }}>{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA row */}
      <div className="mt-12 md:mt-14 flex flex-wrap items-center gap-5">
        <button
          onClick={onStart}
          className="group inline-flex items-center gap-2 rounded-full px-9 py-5 text-[16px] md:text-[17px] font-medium font-sans transition-all hover:translate-y-[-1px]"
          style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 14px 34px -14px rgba(14,28,46,0.4)' }}
          data-testid="c-hero-cta"
        >
          Get Kindra — personal by default
          <ArrowRight size={16} stroke={2} />
        </button>
        <span className="font-sans text-[13px]" style={{ color: NAVY_SOFT }}>
          30 days free · No card · Private by design
        </span>
      </div>
    </div>
  </section>
);

// ─── More comparisons · first scroll ─────────────────────
const MoreComparisons = () => {
  const rows = [
    {
      topic: 'Blood results',
      question: 'What do my blood results mean for me?',
      generic: 'Lab ranges vary. Normal values depend on age and sex. Consult your doctor for interpretation.',
      kindra: 'Your ferritin is 11 — below the lab range of 30–300. Given your fatigue, it\'s worth raising Friday. I\'ve added 3 questions.',
    },
    {
      topic: 'New medication',
      question: 'Is this headache from my new medication?',
      generic: 'Many medications can cause headaches as a side effect. If concerning, contact a medical professional.',
      kindra: 'Your Sertraline started 3 weeks ago — headaches are common early. Your pharmacist can confirm; I\'ve drafted the message.',
    },
    {
      topic: 'Specialist prep',
      question: 'Endocrinologist tomorrow — what should I ask?',
      generic: 'Typical endocrinology questions include thyroid levels, hormone balance, and treatment options.',
      kindra: 'Your TSH is 6.8 (above range). Four questions worth covering: antibodies, treatment now, follow-up timing, monitoring.',
    },
  ];
  return (
    <section className="relative" style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }} data-testid="c-more">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-[760px] mb-12 md:mb-16">
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
            One person. Same day. Every question.
          </p>
          <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[56px] leading-[1.06] tracking-[-0.018em] font-normal" style={{ color: NAVY }} data-testid="c-more-headline">
            Three more questions.<br />
            <em className="italic">Same contrast.</em>
          </h2>
        </div>

        <div className="space-y-4 md:space-y-5">
          {rows.map((r, i) => (
            <article
              key={i}
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
              data-testid={`c-more-${i}`}
            >
              <div className="px-6 md:px-8 py-4 md:py-5 flex items-center justify-between gap-4" style={{ borderBottom: `1px solid ${HAIRLINE}`, backgroundColor: SAGE_BG }}>
                <p className="font-sans text-[10.5px] uppercase font-medium" style={{ color: NAVY_SOFT, letterSpacing: '0.22em' }}>{r.topic}</p>
                <p className="font-serif italic text-[14.5px] md:text-[16px] flex-1 text-right" style={{ color: NAVY }}>"{r.question}"</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ backgroundColor: HAIRLINE }}>
                <div className="p-6 md:p-7" style={{ backgroundColor: '#FFFFFF' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <XMark size={12} color={CORAL} />
                    <span className="font-sans text-[10px] uppercase font-medium" style={{ color: CORAL, letterSpacing: '0.22em' }}>Generic</span>
                  </div>
                  <p className="font-sans text-[14px] leading-[1.55]" style={{ color: NAVY_SOFT }}>{r.generic}</p>
                </div>
                <div className="p-6 md:p-7" style={{ backgroundColor: '#FAF7EF' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <CheckTick size={13} />
                    <span className="font-sans text-[10px] uppercase font-medium" style={{ color: TEAL, letterSpacing: '0.22em' }}>Kindra</span>
                  </div>
                  <p className="font-sans text-[14px] leading-[1.55]" style={{ color: NAVY }}>{r.kindra}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Product proof · what Kindra actually does ───────────
const ProofC = () => {
  const lines = [
    { h: 'Reads your reports', body: 'Pathology, imaging, GP letters — translated into plain English in seconds.' },
    { h: 'Remembers your history', body: 'Conditions, medications, symptoms, notes — all used when you ask a question.' },
    { h: 'Prepares every visit', body: 'A one-page brief with concerns, context, medications and questions worth asking.' },
    { h: 'Stays on your side', body: 'Never prescribes. Never diagnoses. Always supports your clinician, never replaces them.' },
  ];
  return (
    <section className="relative" style={{ backgroundColor: NAVY, color: PAPER }} data-testid="c-proof">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-[720px] mb-12 md:mb-14">
          <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
            How Kindra stays specific
          </p>
          <h2 className="font-serif text-[34px] md:text-[48px] lg:text-[56px] leading-[1.06] tracking-[-0.018em] font-normal">
            It reads. It remembers.<br />
            <em className="italic" style={{ color: TEAL }}>It prepares.</em>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {lines.map((l, i) => (
            <article
              key={i}
              className="rounded-2xl p-7 md:p-8"
              style={{ backgroundColor: 'rgba(251,250,246,0.04)', border: '1px solid rgba(251,250,246,0.1)' }}
              data-testid={`c-proof-${i}`}
            >
              <p className="font-sans text-[10.5px] uppercase font-medium mb-3" style={{ color: TEAL, letterSpacing: '0.22em' }}>0{i + 1}</p>
              <p className="font-serif text-[22px] md:text-[26px] leading-tight mb-3" style={{ color: PAPER }}>{l.h}</p>
              <p className="font-sans text-[14.5px] leading-[1.6]" style={{ color: 'rgba(251,250,246,0.72)' }}>{l.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Trust strip ──────────────────────────────────────────
const TrustC = () => (
  <section className="relative" style={{ backgroundColor: PAPER, borderTop: `1px solid ${HAIRLINE}` }} data-testid="c-trust">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-14 md:py-16">
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 md:gap-x-10 font-sans text-[13px] md:text-[14px]" style={{ color: NAVY_MUTED }}>
        {[
          'Private by design',
          'Never prescribes or diagnoses',
          'Built in Australia',
          'Supports care, never replaces clinicians',
        ].map((l, i) => (
          <span key={i} className="inline-flex items-center gap-2" data-testid={`c-trust-${i}`}>
            <CheckTick size={13} />
            {l}
          </span>
        ))}
      </div>
    </div>
  </section>
);

// ─── Final CTA ───────────────────────────────────────────
const CTAC = ({ onStart }) => (
  <section className="relative" style={{ backgroundColor: SAGE_BG, borderTop: `1px solid ${HAIRLINE}` }} data-testid="c-cta">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 text-center">
      <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>
        Personal by default
      </p>
      <h2 className="font-serif text-[44px] md:text-[64px] lg:text-[76px] leading-[1.04] tracking-[-0.02em] font-normal max-w-[960px] mx-auto" style={{ color: NAVY }}>
        Stop googling your health. <em className="italic" style={{ color: TEAL }}>Start being understood.</em>
      </h2>
      <p className="font-sans mt-7 md:mt-9 text-[16px] md:text-[19px] leading-[1.55] max-w-[560px] mx-auto" style={{ color: NAVY_MUTED }}>
        30 days free. A$9.99/month after. Cancel anytime in one click.
      </p>
      <button
        onClick={onStart}
        className="mt-10 md:mt-12 group inline-flex items-center gap-2 rounded-full px-10 py-5 text-[16px] md:text-[17px] font-medium font-sans transition-colors"
        style={{ backgroundColor: NAVY, color: PAPER, boxShadow: '0 14px 34px -14px rgba(14,28,46,0.4)' }}
        data-testid="c-cta-button"
      >
        Start free
        <ArrowRight size={16} stroke={2} />
      </button>
      <p className="mt-6 font-sans text-[12.5px]" style={{ color: NAVY_SOFT }}>
        Kindra is a health companion — it explains and remembers. It doesn't diagnose, prescribe or replace your clinician.
      </p>
    </div>
  </section>
);

// ─── Page ────────────────────────────────────────────────
const ConceptCPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    try { track('concept_viewed', { concept: 'C' }); } catch { /* noop */ }
  }, []);
  const goStart = () => { try { track('clicked_start_free', { src: 'concept_c' }); } catch {} navigate('/register'); };
  const goLogin = () => navigate('/login');
  return (
    <div className="min-h-screen font-sans" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="concept-c-page">
      <ConceptNav onStart={goStart} onLogin={goLogin} user={user} activeConcept="C" />
      <HeroC onStart={goStart} />
      <MoreComparisons />
      <ProofC />
      <TrustC />
      <CTAC onStart={goStart} />
      <ConceptFooter />
    </div>
  );
};

export default ConceptCPage;
